import { useMutation, useQueryClient } from "@tanstack/react-query";
import { produce } from "immer";
import { toast } from "react-toastify";

import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import getQueryKeys from "common/datahooks/getQueryKeys";
import { HTTPError, post } from "common/helpers/HTTP";
import { typedObjectEntries } from "common/helpers/utils";

import { useCampaignContext } from "../context/CampaignContext";
import useUpdateCampaign from "./useUpdateCampaign";

import type {
  AudienceFilters,
  AudienceProfile,
  CancelledTarget,
} from "../types";
import type { AudienceSearchResponse } from "./useAudienceSearch";

async function addTargetToCampaignRequest(
  workspaceId: string,
  campaignId: string,
  profileUrl: string,
): Promise<AudienceProfile> {
  return (
    await post(`workspaces/${workspaceId}/campaigns/${campaignId}/audience`, {
      profile_url: profileUrl,
    })
  ).profile;
}

async function removeProfileFromAudienceRequest(
  workspaceId: string,
  campaignId: string,
  options: { targetUrns: string[] } | { filters: AudienceFilters },
): Promise<{
  removed: string[];
  cancelled: string[];
}> {
  let payload = {};

  if ("targetUrns" in options) {
    payload = { target_urns: options.targetUrns };
  } else {
    const nonEmptyFilters = typedObjectEntries(options.filters)
      .filter(([, value]) => {
        if (Array.isArray(value)) return value.length > 0;
        if (typeof value === "string") return value.trim() !== "";
        return value;
      })
      // biome-ignore lint/performance/noAccumulatingSpread: Small filter object, O(n²) is acceptable
      .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

    payload = Object.keys(nonEmptyFilters).length > 0 ? nonEmptyFilters : {};
  }

  const { removed, cancelled } = await post(
    `workspaces/${workspaceId}/campaigns/${campaignId}/audience-delete`,
    payload,
  );

  return { removed, cancelled };
}

async function exportAudienceRequest(
  workspaceId: string,
  campaignId: string,
  filters: AudienceFilters,
): Promise<string> {
  const response = await post(
    `workspaces/${workspaceId}/campaigns/${campaignId}/audience-export`,
    filters,
  );

  return response.url;
}

export default function useAudienceMutations(filters?: AudienceFilters) {
  const queryClient = useQueryClient();
  const { id: workspaceId } = useSelectedWorkspaceContext();
  const { campaignsKeys } = getQueryKeys(workspaceId);
  const { updateCachedCampaign, updateCachedCampaignInList } =
    useUpdateCampaign();
  const { id: campaignId, state: campaignState } = useCampaignContext();

  const { mutateAsync: removeProfiles, isPending: isRemovingProfiles } =
    useMutation({
      mutationFn: (
        options: { targetUrns: string[] } | { filters: AudienceFilters },
      ) => removeProfileFromAudienceRequest(workspaceId, campaignId, options),
      onSuccess: ({ removed, cancelled }) => {
        const profilesCount = removed.length + cancelled.length;

        // Update audience data (Campaign Details)
        if (queryClient.getQueryData(campaignsKeys.audience(campaignId))) {
          queryClient.setQueryData(
            campaignsKeys.audience(campaignId),
            (prevAudience: AudienceProfile[]) => {
              const filteredProfiles = prevAudience.filter(
                ({ urn }) => !removed.includes(urn),
              );
              if (cancelled.length) {
                return produce(filteredProfiles, (draftProfiles) => {
                  draftProfiles.forEach((draftProfile) => {
                    if (cancelled.includes(draftProfile.urn)) {
                      draftProfile.state = "cancelled";
                    }
                  });
                });
              }

              return filteredProfiles;
            },
          );
        }

        if (
          cancelled.length &&
          queryClient.getQueryData(campaignsKeys.cancelledAudience(campaignId))
        ) {
          queryClient.setQueryData<CancelledTarget[]>(
            campaignsKeys.cancelledAudience(campaignId),
            (prevCancelledTargets) => [
              ...prevCancelledTargets,
              ...cancelled.map((urn) => ({
                target_urn: urn,
                transition: "manual_cancel" as const,
              })),
            ],
          );
        }

        if (filters) {
          const audienceSearchQueries = queryClient.getQueryCache().findAll({
            queryKey: campaignsKeys.audienceSearch(campaignId, filters),
          });

          if (audienceSearchQueries.length) {
            for (const { queryKey } of audienceSearchQueries) {
              queryClient.setQueryData<AudienceSearchResponse>(
                queryKey,
                (prev) => {
                  if (!prev) return prev;

                  return {
                    ...prev,
                    data: prev.data.filter(({ urn }) => !removed.includes(urn)),
                    total: Math.max(0, (prev.total ?? 0) - removed.length),
                  };
                },
              );
            }
          }
        }

        toast.success(
          `${profilesCount} target${profilesCount > 1 ? "s" : ""} removed`,
        );
      },
    });

  const {
    mutateAsync: addTargetToCampaign,
    isPending: isAddingTargetToCampaign,
  } = useMutation({
    mutationFn: ({ profileUrl }: { profileUrl: string }) =>
      addTargetToCampaignRequest(workspaceId, campaignId, profileUrl),
    onSuccess: (profile) => {
      if (queryClient.getQueryData(campaignsKeys.audience(campaignId))) {
        queryClient.setQueryData(
          campaignsKeys.audience(campaignId),
          (prevAudience: AudienceProfile[]) => [...prevAudience, profile],
        );
      }
      updateCachedCampaignInList(
        campaignId,
        campaignState === "DONE" ? { state: "ACTIVE" } : { state: "DONE" },
      );
      updateCachedCampaign(campaignId, (draftCampaign) => {
        if (draftCampaign.state === "DONE") {
          draftCampaign.state = "ACTIVE";
        }
      });

      toast.success("Target added to the campaign");
    },
    onError: (err) => {
      toast.error(
        err instanceof HTTPError && err.serverMessage
          ? err.serverMessage
          : "Failed to add target to the campaign",
      );
    },
  });

  const { mutateAsync: exportAudience, isPending: isExporting } = useMutation({
    mutationFn: (audienceFilters: AudienceFilters) =>
      exportAudienceRequest(workspaceId, campaignId, audienceFilters),
    onError: () => {
      // Show an error toast to the user
      toast.error(
        "Failed to export audience, please try again later or contact support.",
      );
    },
  });

  return {
    removeProfiles,
    isRemovingProfiles,
    addTargetToCampaign,
    isAddingTargetToCampaign,
    exportAudience,
    isExporting,
  };
}
